import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Layout } from '../components/Layout';
import { StatusBadge, PriorityBadge } from '../components/StatusBadge';
import { formatDate } from '../utils/dateUtils';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Checkbox } from '../components/ui/checkbox';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import { CheckCircle, XCircle, Clock, Loader2, Eye, FileCheck, CheckSquare } from 'lucide-react';
import { RequestDetailsSection } from '../components/RequestDetailsSection';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ApprovalsPage() {
  const navigate = useNavigate();
  const { getAuthHeader, employee } = useAuth();
  const [pendingTickets, setPendingTickets] = useState([]);
  const [approvedTickets, setApprovedTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [approveDialog, setApproveDialog] = useState(false);
  const [rejectDialog, setRejectDialog] = useState(false);
  const [approvalRemarks, setApprovalRemarks] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  
  // Bulk selection state
  const [selectedTicketIds, setSelectedTicketIds] = useState([]);
  const [bulkApproveDialog, setBulkApproveDialog] = useState(false);
  const [bulkRejectDialog, setBulkRejectDialog] = useState(false);
  const [bulkRemarks, setBulkRemarks] = useState('');
  const [bulkRejectionReason, setBulkRejectionReason] = useState('');

  const fetchApprovals = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch pending approvals
      const pendingRes = await fetch(`${API}/tickets?pending_approval=true`, {
        headers: getAuthHeader(),
      });

      if (pendingRes.ok) {
        const data = await pendingRes.json();
        setPendingTickets(data);
      }

      // Fetch ALL tickets this user has approved (any status)
      const approvedRes = await fetch(`${API}/tickets/my-approved`, {
        headers: getAuthHeader(),
      });

      if (approvedRes.ok) {
        const data = await approvedRes.json();
        setApprovedTickets(data);
      }
    } catch (error) {
      console.error('Fetch approvals error:', error);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  useEffect(() => {
    fetchApprovals();
  }, [fetchApprovals]);

  const handleApprove = async () => {
    if (!selectedTicket) return;
    
    setActionLoading(true);
    try {
      const res = await fetch(
        `${API}/tickets/${selectedTicket.ticket_id}/approve?remarks=${encodeURIComponent(approvalRemarks)}`,
        {
          method: 'PUT',
          headers: getAuthHeader(),
          
        }
      );

      if (res.ok) {
        toast.success('Ticket approved successfully');
        setApproveDialog(false);
        setSelectedTicket(null);
        setApprovalRemarks('');
        fetchApprovals();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to approve ticket');
      }
    } catch (error) {
      toast.error('Failed to approve ticket');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!selectedTicket) return;
    
    if (!rejectionReason.trim()) {
      toast.error('Rejection reason is required');
      return;
    }

    setActionLoading(true);
    try {
      const res = await fetch(
        `${API}/tickets/${selectedTicket.ticket_id}/reject?reason=${encodeURIComponent(rejectionReason)}`,
        {
          method: 'PUT',
          headers: getAuthHeader(),
          
        }
      );

      if (res.ok) {
        toast.success('Ticket rejected');
        setRejectDialog(false);
        setSelectedTicket(null);
        setRejectionReason('');
        fetchApprovals();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to reject ticket');
      }
    } catch (error) {
      toast.error('Failed to reject ticket');
    } finally {
      setActionLoading(false);
    }
  };

  // Bulk action handlers
  const toggleTicketSelection = (ticketId) => {
    setSelectedTicketIds(prev => 
      prev.includes(ticketId) 
        ? prev.filter(id => id !== ticketId)
        : [...prev, ticketId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedTicketIds.length === pendingTickets.length) {
      setSelectedTicketIds([]);
    } else {
      setSelectedTicketIds(pendingTickets.map(t => t.ticket_id));
    }
  };

  const handleBulkApprove = async () => {
    if (selectedTicketIds.length === 0) return;
    
    setActionLoading(true);
    try {
      const res = await fetch(`${API}/tickets/bulk/approve`, {
        method: 'PUT',
        headers: {
          ...getAuthHeader(),
          'Content-Type': 'application/json'
        },
        
        body: JSON.stringify({ 
          ticket_ids: selectedTicketIds,
          remarks: bulkRemarks || 'Bulk approved'
        })
      });

      if (res.ok) {
        const data = await res.json();
        toast.success(data.message);
        setBulkApproveDialog(false);
        setSelectedTicketIds([]);
        setBulkRemarks('');
        fetchApprovals();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Bulk approve failed');
      }
    } catch (error) {
      toast.error('Failed to bulk approve');
    } finally {
      setActionLoading(false);
    }
  };

  const handleBulkReject = async () => {
    if (selectedTicketIds.length === 0) return;
    if (!bulkRejectionReason.trim()) {
      toast.error('Rejection reason is required');
      return;
    }
    
    setActionLoading(true);
    try {
      const res = await fetch(`${API}/tickets/bulk/reject`, {
        method: 'PUT',
        headers: {
          ...getAuthHeader(),
          'Content-Type': 'application/json'
        },
        
        body: JSON.stringify({ 
          ticket_ids: selectedTicketIds,
          remarks: bulkRejectionReason
        })
      });

      if (res.ok) {
        const data = await res.json();
        toast.success(data.message);
        setBulkRejectDialog(false);
        setSelectedTicketIds([]);
        setBulkRejectionReason('');
        fetchApprovals();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Bulk reject failed');
      }
    } catch (error) {
      toast.error('Failed to bulk reject');
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <Layout>
      <div className="space-y-6" data-testid="approvals-page">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Approvals</h1>
          <p className="text-muted-foreground mt-1">
            Review and approve requests from your team members
          </p>
        </div>

        {/* Pending Approvals */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="w-5 h-5" />
                  Pending Approvals
                </CardTitle>
                <CardDescription>
                  {pendingTickets.length} ticket{pendingTickets.length !== 1 ? 's' : ''} awaiting your approval
                </CardDescription>
              </div>
              {selectedTicketIds.length > 0 && (
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">
                    {selectedTicketIds.length} selected
                  </span>
                  <Button
                    size="sm"
                    className="bg-green-600 hover:bg-green-700 gap-1"
                    onClick={() => setBulkApproveDialog(true)}
                    data-testid="bulk-approve-btn"
                  >
                    <CheckSquare className="w-4 h-4" />
                    Bulk Approve
                  </Button>
                  <Button
                    size="sm"
                    variant="destructive"
                    className="gap-1"
                    onClick={() => setBulkRejectDialog(true)}
                    data-testid="bulk-reject-btn"
                  >
                    <XCircle className="w-4 h-4" />
                    Bulk Reject
                  </Button>
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-6 space-y-3">
                {[1, 2, 3].map(i => (
                  <Skeleton key={i} className="h-12 w-full" />
                ))}
              </div>
            ) : pendingTickets.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground empty-state">
                <CheckCircle className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p className="font-medium">No pending approvals</p>
                <p className="text-sm mt-1">All caught up!</p>
              </div>
            ) : (
              <div className="overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/50">
                      <TableHead className="w-[40px]">
                        <Checkbox
                          checked={selectedTicketIds.length === pendingTickets.length && pendingTickets.length > 0}
                          onCheckedChange={toggleSelectAll}
                          data-testid="select-all-checkbox"
                        />
                      </TableHead>
                      <TableHead>Ticket ID</TableHead>
                      <TableHead>Request Type</TableHead>
                      <TableHead>Requester</TableHead>
                      <TableHead className="hidden sm:table-cell">Priority</TableHead>
                      <TableHead className="hidden md:table-cell">Date</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {pendingTickets.map((ticket) => (
                      <TableRow key={ticket.ticket_id} className="table-row-hover">
                        <TableCell>
                          <Checkbox
                            checked={selectedTicketIds.includes(ticket.ticket_id)}
                            onCheckedChange={() => toggleTicketSelection(ticket.ticket_id)}
                            data-testid={`select-${ticket.ticket_id}`}
                          />
                        </TableCell>
                        <TableCell>
                          <button
                            onClick={() => navigate(`/tickets/${ticket.ticket_id}`)}
                            className="ticket-id-link font-mono text-sm"
                          >
                            {ticket.ticket_id}
                          </button>
                        </TableCell>
                        <TableCell className="font-medium max-w-[150px] truncate">
                          {ticket.request_type === 'Other' 
                            ? ticket.other_request_type 
                            : ticket.request_type}
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {ticket.requester_name}
                        </TableCell>
                        <TableCell className="hidden sm:table-cell">
                          <PriorityBadge priority={ticket.priority} />
                        </TableCell>
                        <TableCell className="hidden md:table-cell text-muted-foreground text-sm">
                          {formatDate(ticket.created_at)}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => navigate(`/tickets/${ticket.ticket_id}`)}
                              data-testid={`view-${ticket.ticket_id}`}
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              className="bg-green-600 hover:bg-green-700 gap-1"
                              onClick={() => {
                                setSelectedTicket(ticket);
                                setApproveDialog(true);
                              }}
                              data-testid={`approve-${ticket.ticket_id}`}
                            >
                              <CheckCircle className="w-4 h-4" />
                              <span className="hidden sm:inline">Approve</span>
                            </Button>
                            <Button
                              size="sm"
                              variant="destructive"
                              className="gap-1"
                              onClick={() => {
                                setSelectedTicket(ticket);
                                setRejectDialog(true);
                              }}
                              data-testid={`reject-${ticket.ticket_id}`}
                            >
                              <XCircle className="w-4 h-4" />
                              <span className="hidden sm:inline">Reject</span>
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* All Approved Tickets Table */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileCheck className="w-5 h-5" />
              All Approved Tickets
            </CardTitle>
            <CardDescription>
              All tickets you have approved ({approvedTickets.length})
            </CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-6 space-y-3">
                {[1, 2, 3].map(i => (
                  <Skeleton key={i} className="h-12 w-full" />
                ))}
              </div>
            ) : approvedTickets.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <p className="text-sm">No approved requests yet</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/50">
                      <TableHead>Ticket ID</TableHead>
                      <TableHead>Request Type</TableHead>
                      <TableHead>Requester</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Assigned Admin</TableHead>
                      <TableHead>Created Date</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {approvedTickets.map((ticket) => (
                      <TableRow 
                        key={ticket.ticket_id} 
                        className="table-row-hover cursor-pointer"
                        onClick={() => navigate(`/tickets/${ticket.ticket_id}`)}
                      >
                        <TableCell className="ticket-id-link font-mono text-sm">
                          {ticket.ticket_id}
                        </TableCell>
                        <TableCell className="font-medium max-w-[150px] truncate">
                          {ticket.request_type === 'Other' 
                            ? ticket.other_request_type 
                            : ticket.request_type}
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {ticket.requester_name}
                        </TableCell>
                        <TableCell>
                          <StatusBadge status={ticket.status} />
                        </TableCell>
                        <TableCell className="text-muted-foreground text-sm">
                          {ticket.assigned_admin_name || '-'}
                        </TableCell>
                        <TableCell className="text-muted-foreground text-sm">
                          {formatDate(ticket.created_at)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Approve Dialog */}
        <Dialog open={approveDialog} onOpenChange={setApproveDialog}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Approve Ticket</DialogTitle>
              <DialogDescription>
                Approve {selectedTicket?.ticket_id} - {selectedTicket?.request_type}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="p-4 bg-muted rounded-lg">
                <p className="text-sm font-medium">{selectedTicket?.requester_name}</p>
                <p className="text-sm text-muted-foreground mt-1">
                  {selectedTicket?.description}
                </p>
              </div>
              
              {/* Request Details Section */}
              {selectedTicket && <RequestDetailsSection ticket={selectedTicket} compact />}
              
              <div className="space-y-2">
                <Label>Remarks (Optional)</Label>
                <Textarea
                  value={approvalRemarks}
                  onChange={(e) => setApprovalRemarks(e.target.value)}
                  placeholder="Add any remarks for approval..."
                  data-testid="approval-remarks-textarea"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setApproveDialog(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleApprove} 
                disabled={actionLoading}
                className="bg-green-600 hover:bg-green-700"
                data-testid="confirm-approve-dialog-btn"
              >
                {actionLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Approve
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Reject Dialog */}
        <Dialog open={rejectDialog} onOpenChange={setRejectDialog}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Reject Ticket</DialogTitle>
              <DialogDescription>
                Reject {selectedTicket?.ticket_id} - {selectedTicket?.request_type}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="p-4 bg-muted rounded-lg">
                <p className="text-sm font-medium">{selectedTicket?.requester_name}</p>
                <p className="text-sm text-muted-foreground mt-1">
                  {selectedTicket?.description}
                </p>
              </div>
              
              {/* Request Details Section */}
              {selectedTicket && <RequestDetailsSection ticket={selectedTicket} compact />}
              
              <div className="space-y-2">
                <Label>Rejection Reason *</Label>
                <Textarea
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  placeholder="Enter the reason for rejection..."
                  data-testid="rejection-reason-textarea"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setRejectDialog(false)}>
                Cancel
              </Button>
              <Button 
                variant="destructive"
                onClick={handleReject} 
                disabled={actionLoading}
                data-testid="confirm-reject-dialog-btn"
              >
                {actionLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Reject
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Bulk Approve Dialog */}
        <Dialog open={bulkApproveDialog} onOpenChange={setBulkApproveDialog}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Bulk Approve Tickets</DialogTitle>
              <DialogDescription>
                Approve {selectedTicketIds.length} selected ticket{selectedTicketIds.length !== 1 ? 's' : ''}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="p-3 bg-muted rounded-lg text-sm">
                <p className="font-medium mb-1">Selected Tickets:</p>
                <p className="text-muted-foreground">{selectedTicketIds.join(', ')}</p>
              </div>
              <div className="space-y-2">
                <Label>Remarks (Optional)</Label>
                <Textarea
                  value={bulkRemarks}
                  onChange={(e) => setBulkRemarks(e.target.value)}
                  placeholder="Add remarks for all selected tickets..."
                  data-testid="bulk-approve-remarks"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setBulkApproveDialog(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleBulkApprove} 
                disabled={actionLoading}
                className="bg-green-600 hover:bg-green-700"
                data-testid="confirm-bulk-approve-btn"
              >
                {actionLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Approve {selectedTicketIds.length} Ticket{selectedTicketIds.length !== 1 ? 's' : ''}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Bulk Reject Dialog */}
        <Dialog open={bulkRejectDialog} onOpenChange={setBulkRejectDialog}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Bulk Reject Tickets</DialogTitle>
              <DialogDescription>
                Reject {selectedTicketIds.length} selected ticket{selectedTicketIds.length !== 1 ? 's' : ''}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="p-3 bg-muted rounded-lg text-sm">
                <p className="font-medium mb-1">Selected Tickets:</p>
                <p className="text-muted-foreground">{selectedTicketIds.join(', ')}</p>
              </div>
              <div className="space-y-2">
                <Label>Rejection Reason *</Label>
                <Textarea
                  value={bulkRejectionReason}
                  onChange={(e) => setBulkRejectionReason(e.target.value)}
                  placeholder="Enter the reason for rejecting all selected tickets..."
                  data-testid="bulk-reject-reason"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setBulkRejectDialog(false)}>
                Cancel
              </Button>
              <Button 
                variant="destructive"
                onClick={handleBulkReject} 
                disabled={actionLoading}
                data-testid="confirm-bulk-reject-btn"
              >
                {actionLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Reject {selectedTicketIds.length} Ticket{selectedTicketIds.length !== 1 ? 's' : ''}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
